/** @constant Fallback when theme settings are blank (matches CHATBOT defaults in settings_schema). */
var CHATBOT_DEFAULT_EMEA_DEPLOYMENT_ID = '90a18031-9c6e-4021-a73b-08b5e7b22f33';

/**
 * Market handles that always use the theme setting `us_countries_id`. Others use `emea_countries_id`.
 * Optional extras merge via `window.chatbot.usMarketHandlesRaw`.
 */
var GENESYS_DEFAULT_US_MARKET_HANDLES = ['en-us', 'en-mx', 'en-ca'];

function buildLanguageCountry(locale, country) {
    if (!locale && !country) return '';
    if (!locale) return country || '';
    if (!country) return locale;

    const normalizedCountry = country.toUpperCase();
    const localeParts = locale.split('-');
    const lastPart = localeParts[localeParts.length - 1] || '';

    return lastPart.toUpperCase() === normalizedCountry ? locale : `${locale}-${normalizedCountry}`;
}

function sendGenesysTealiumEvent(requestedData = {}) {
    const sourceData = requestedData && typeof requestedData === 'object' && !Array.isArray(requestedData)
        ? requestedData
        : {};
    const localPayload = { ...sourceData };

    if (typeof window.sendTealiumEvent === 'function') {
        window.sendTealiumEvent(localPayload);
    } else if (typeof window.tealium_data2track?.push === 'function') {
        window.tealium_data2track.push(localPayload);
    }
}

/**
 * Parses comma-separated market handles from theme settings.
 * @param {string} [raw]
 * @returns {string[]}
 */
function parseUsMarketHandles(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
}

/**
 * Normalizes a Shopify market handle for comparison (lowercase, trim, underscores → hyphens).
 * @param {string} [s]
 * @returns {string}
 */
function normalizeMarketHandleToken(s) {
    if (!s || typeof s !== 'string') return '';
    return s
        .trim()
        .toLowerCase()
        .replace(/_/g, '-');
}

/**
 * Set of normalized market handles that use `us_countries_id`: defaults (en-us, en-mx, en-ca) plus theme extras.
 * @param {object} [bot]
 * @returns {Set<string>}
 */
function getGenesysUsMarketHandleSet(bot) {
    const fromTheme = parseUsMarketHandles((bot && bot.usMarketHandlesRaw) || '').map(
        normalizeMarketHandleToken
    );
    return new Set([
        ...GENESYS_DEFAULT_US_MARKET_HANDLES.map(normalizeMarketHandleToken),
        ...fromTheme,
    ]);
}

/**
 * First URL path segment when it looks like a Shopify market prefix (e.g. `/en-us/pages/...` → `en-us`).
 * `localization.market.handle` often differs from this; matching the path fixes NA routing.
 * @returns {string} Normalized token or empty if not a `xx-yy` prefix.
 */
function getStorefrontMarketPrefixFromPath() {
    if (typeof window === 'undefined' || !window.location || !window.location.pathname) {
        return '';
    }
    const first = window.location.pathname.split('/').filter(Boolean)[0] || '';
    if (!/^[a-z]{2}-[a-z]{2}$/i.test(first)) {
        return '';
    }
    return normalizeMarketHandleToken(first);
}

/**
 * True for US-region markets (en-us, en-mx, en-ca) → `us_countries_id`; else `emea_countries_id`.
 * Uses both `localization.market.handle` and the storefront URL prefix when present.
 * @param {object} bot
 * @returns {boolean}
 */
function shouldUseUsGenesysDeployment(bot) {
    const handleFromApi = normalizeMarketHandleToken(
        typeof window.poShopifyLocalization !== 'undefined' &&
            window.poShopifyLocalization.market &&
            window.poShopifyLocalization.market.handle
            ? String(window.poShopifyLocalization.market.handle)
            : ''
    );

    const handleFromPath = getStorefrontMarketPrefixFromPath();
    const usSet = getGenesysUsMarketHandleSet(bot || {});

    if (handleFromApi && usSet.has(handleFromApi)) return true;
    if (handleFromPath && usSet.has(handleFromPath)) return true;

    return false;
}

/**
 * Resolves the Genesys deployment ID from theme settings, country, and optional US market handles.
 * @returns {string}
 */
function getGenesysDeploymentIdForCurrentCountry() {
    const bot = window.chatbot;
    if (!bot) return '';

    if (shouldUseUsGenesysDeployment(bot) && bot.usCountriesId) {
        return String(bot.usCountriesId).trim();
    }

    let emea = bot.emeaCountriesId ? String(bot.emeaCountriesId).trim() : '';
    if (!emea) {
        emea = CHATBOT_DEFAULT_EMEA_DEPLOYMENT_ID;
    }
    return emea;
}

/**
 * Logs current Shopify market + whether Genesys routes to US or EMEA and the deployment ID in use.
 */
function logGenesysMarketRouting() {
    if (typeof console === 'undefined' || !console.log) return;

    const bot = window.chatbot;
    const marketHandleApi =
        typeof window.poShopifyLocalization !== 'undefined' &&
        window.poShopifyLocalization.market &&
        window.poShopifyLocalization.market.handle
            ? String(window.poShopifyLocalization.market.handle)
            : '(unknown)';
    const marketHandlePath = getStorefrontMarketPrefixFromPath() || '(no xx-yy path prefix)';
    const country =
        typeof Shopify !== 'undefined' && Shopify.country ? String(Shopify.country) : '(unknown)';
    const useUs = shouldUseUsGenesysDeployment(bot || {});
    const region = useUs ? 'US' : 'EMEA';
    const deploymentId = String(GenesysChat.constants.chatBotDeploymentId || '').trim();

    console.log('[GenesysChat] Market routing', {
        marketHandleFromShopify: marketHandleApi,
        marketPrefixFromUrlPath: marketHandlePath,
        country,
        region,
        deploymentId,
        usMarketHandlesResolved: Array.from(getGenesysUsMarketHandleSet(bot || {})),
    });
}

var GenesysChat = {
    messengerCloseTimeout: null,
    conversationCloseTimeout: null,
    scrollHandlerBound: false,

    constants: {
        chatBotDeploymentId: getGenesysDeploymentIdForCurrentCountry(),
        country: Shopify.country,
        languageCountry: buildLanguageCountry(Shopify.locale, Shopify.country),
        genesysApp: '.genesys-app',
        environment: 'prod-euw1',
        genesysUrl: "https://apps.mypurecloud.com/genesys-bootstrap/genesys.min.js",
        chatEndedKey: 'chatbotEnded',
        chatClosedKey: 'chatbotClosed',
        messengerCloseDelay: 250
    },

    init: function () {
        logGenesysMarketRouting();

        const deploymentId = String(GenesysChat.constants.chatBotDeploymentId || '').trim();
        if (!deploymentId) {
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[GenesysChat] No deployment ID configured; bootstrap skipped.');
            }
            return;
        }

        subscribe("change-country", () => {
            Genesys("command", "Messenger.clear")
        });

        const languageCountry = buildLanguageCountry(Shopify.locale, Shopify.country);

        (function (g, e, n, es, ys) {
            g['_genesysJs'] = e;
            g[e] =
                g[e] ||
                function () {
                    (g[e].q = g[e].q || []).push(arguments);
                };
            g[e].t = 1 * new Date();
            g[e].c = es;
            ys = document.createElement('script');
            ys.async = 1;
            ys.src = n;
            ys.charset = 'utf-8';
            document.head.appendChild(ys);
        })(window, 'Genesys', GenesysChat.constants.genesysUrl, {
            environment: GenesysChat.constants.environment,
            deploymentId: GenesysChat.constants.chatBotDeploymentId,
            debug: false
        });

        Genesys("command", "Database.set", {
            messaging: {
                customAttributes: {
                    "webmsg_brandcode": 'PO',
                    "webmsg_country": Shopify.country,
                    "webmsg_language": languageCountry
                }
            }
        })

        let flag = true;
        const observer = new MutationObserver(() => {
            if (flag) {
                flag = false;
                GenesysChat.buttonDisplaySettings();
                observer.disconnect();
            }
        });
        observer.observe(document.body, {childList: true});

        this.bind();
    },

    bind: function () {

        Genesys('subscribe', 'Messenger.ready', function (o) {
            setTimeout(() => {
                if (GenesysChat.hasActiveChat()) {
                    GenesysChat.resumeChat();
                }

                GenesysChat.updateLauncherVisibility();
            }, 1000);
        });

        Genesys('subscribe', 'MessagingService.messagesReceived', function (o) {
            const first = o && o.data && o.data.messages && o.data.messages[0];
            if (!first || first.direction !== 'Inbound') return;

            sendGenesysTealiumEvent({
                'id': 'Click',
                'data_element_id': 'X_X_ChatBot_Send',
                'data_analytics_available_call': '1',
            });
        });

        Genesys('subscribe', 'Conversations.opened', function (o) {
            GenesysChat.clearMessengerCloseTimeout();
            GenesysChat.clearConversationCloseTimeout();
            GenesysChat.markChatStarted();
            GenesysChat.displayChatbotElement("none");
            GenesysChat.showDefaultLauncher();
        });

        Genesys('subscribe', 'Messenger.closed', function (o) {
            GenesysChat.handleMessengerClosed();
        });

        Genesys('subscribe', 'Conversations.closed', function (o) {
            GenesysChat.handleConversationMinimized();
        });

        Genesys('subscribe', 'Conversations.ended', function (o) {
            GenesysChat.handleConversationEnded();
        });
    },

    hasActiveChat: function () {
        return sessionStorage.getItem("showHelpChatbot") != null &&
            sessionStorage.getItem(GenesysChat.constants.chatEndedKey) !== "true" &&
            sessionStorage.getItem(GenesysChat.constants.chatClosedKey) !== "true";
    },

    hasChatEnded: function () {
        return sessionStorage.getItem(GenesysChat.constants.chatEndedKey) === "true";
    },

    isChatMinimized: function () {
        return sessionStorage.getItem("showHelpChatbot") != null &&
            sessionStorage.getItem(GenesysChat.constants.chatClosedKey) === "true" &&
            !GenesysChat.hasChatEnded();
    },

    markChatStarted: function () {
        sessionStorage.setItem("showHelpChatbot", "true");
        sessionStorage.removeItem(GenesysChat.constants.chatEndedKey);
        sessionStorage.removeItem(GenesysChat.constants.chatClosedKey);
    },

    markChatEnded: function () {
        sessionStorage.removeItem("showHelpChatbot");
        sessionStorage.setItem(GenesysChat.constants.chatEndedKey, "true");
        sessionStorage.setItem(GenesysChat.constants.chatClosedKey, "true");
    },

    markChatClosed: function () {
        sessionStorage.setItem(GenesysChat.constants.chatClosedKey, "true");
    },

    clearMessengerCloseTimeout: function () {
        if (GenesysChat.messengerCloseTimeout == null) return;

        window.clearTimeout(GenesysChat.messengerCloseTimeout);
        GenesysChat.messengerCloseTimeout = null;
    },

    clearConversationCloseTimeout: function () {
        if (GenesysChat.conversationCloseTimeout == null) return;

        window.clearTimeout(GenesysChat.conversationCloseTimeout);
        GenesysChat.conversationCloseTimeout = null;
    },

    getDefaultLauncher: function () {
        return document.getElementById('genesys-mxg-frame');
    },

    hideDefaultLauncher: function () {
        const launcher = GenesysChat.getDefaultLauncher();
        if (!launcher) return;

        launcher.style.setProperty('display', 'none', 'important');
        launcher.style.setProperty('visibility', 'hidden', 'important');
        launcher.style.setProperty('opacity', '0', 'important');
        launcher.style.setProperty('pointer-events', 'none', 'important');
    },

    showDefaultLauncher: function () {
        const launcher = GenesysChat.getDefaultLauncher();
        if (!launcher) return;

        launcher.style.removeProperty('display');
        launcher.style.removeProperty('visibility');
        launcher.style.removeProperty('opacity');
        launcher.style.removeProperty('pointer-events');
    },

    shouldShowDefaultLauncher: function (hasChatSession) {
        return hasChatSession &&
            !GenesysChat.hasChatEnded() &&
            !GenesysChat.isChatMinimized();
    },

    updateLauncherVisibility: function () {
        const hasChatSession = sessionStorage.getItem("showHelpChatbot") != null;
        const hasChatEnded = GenesysChat.hasChatEnded();
        const shouldShowDefaultLauncher = GenesysChat.shouldShowDefaultLauncher(hasChatSession);
        const shouldShowSideLauncher = !hasChatEnded && !shouldShowDefaultLauncher;

        GenesysChat.displayChatbotElement(shouldShowSideLauncher ? "flex" : "none");

        if (shouldShowDefaultLauncher) {
            GenesysChat.showDefaultLauncher();
        } else {
            GenesysChat.hideDefaultLauncher();
        }
    },

    handleMessengerClosed: function () {
        if (GenesysChat.isChatMinimized() || GenesysChat.hasChatEnded()) return;

        GenesysChat.clearMessengerCloseTimeout();
        GenesysChat.messengerCloseTimeout = window.setTimeout(() => {
            GenesysChat.messengerCloseTimeout = null;
            GenesysChat.handleConversationEnded();
        }, GenesysChat.constants.messengerCloseDelay);
    },

    handleConversationMinimized: function () {
        GenesysChat.markChatClosed();
        GenesysChat.clearMessengerCloseTimeout();
        GenesysChat.clearConversationCloseTimeout();
        GenesysChat.conversationCloseTimeout = window.setTimeout(() => {
            GenesysChat.conversationCloseTimeout = null;

            if (GenesysChat.hasChatEnded()) return;

            if (sessionStorage.getItem("showHelpChatbot") != null) {
                GenesysChat.updateLauncherVisibility();
            }

            sendGenesysTealiumEvent({
                'id': 'Click',
                'data_element_id': 'X_X_ChatBot_Minimize',
                'data_analytics_available_call': '1',
            });
        }, GenesysChat.constants.messengerCloseDelay);
    },

    handleConversationEnded: function () {
        GenesysChat.clearMessengerCloseTimeout();
        GenesysChat.clearConversationCloseTimeout();

        if (GenesysChat.hasChatEnded()) return;

        GenesysChat.markChatEnded();
        GenesysChat.updateLauncherVisibility();

        sendGenesysTealiumEvent({
            'id': 'Click',
            'data_element_id': 'X_X_ChatBot_Close',
            'data_analytics_available_call': '1',
        });
    },

    resumeChat: function () {
        Genesys("command", "Messenger.open", {},
            function (o) {
                // ok
            },
            function (o) {
                // error
            }
        );
    },

    startChatbot: function (e) {
        // Avvio della chat Genesys
        const languageCountry = buildLanguageCountry(Shopify.locale, Shopify.country);

        Genesys("command", "Database.set", {
            messaging: {
                customAttributes: {
                    "webmsg_brandcode": 'PO',
                    "webmsg_country": Shopify.country,
                    "webmsg_language": languageCountry
                }
            }
        });
        GenesysChat.markChatStarted();
        GenesysChat.displayChatbotElement("none");
        GenesysChat.showDefaultLauncher();
        GenesysChat.resumeChat();
        GenesysChat.buttonDisplaySettings();
    },

    buttonDisplaySettings: function () {
        if (!GenesysChat.scrollHandlerBound) {
            document.addEventListener("scroll", GenesysChat.updateLauncherVisibility);
            GenesysChat.scrollHandlerBound = true;
        }

        GenesysChat.updateLauncherVisibility();
    },

    openChat: function (e) {
        if (document.getElementById('genesys-mxg-container-frame')) {
            sendGenesysTealiumEvent({
                'id': 'Click',
                'data_element_id': 'X_X_ChatBot_Open',
                'data_analytics_available_call': '1',
            });
            GenesysChat.startChatbot();
            GenesysChat.markChatStarted();
        }
    },

    displayChatbotElement(display) {
        const chatbot = document.querySelector("#chatbot-element");
        if (!chatbot) return;
        chatbot.style.display = display;
    }
}

GenesysChat.init();
